import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { INVOICE_DEFAULTS, PAYMENT_METHODS, PAYMENT_STATUSES } from "@/lib/printTemplates";
import { InvoiceDoc } from "@/components/print/InvoiceDoc";
import { invoiceTpl, printInvoice, downloadInvoicePdf, loadConfig } from "@/lib/printing";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Printer, FileDown, Ban, Trash2 } from "lucide-react";

const emptyCustomer = { name: "", company: "", eik: "", vat_number: "", address: "", mol: "", email: "" };
const Sel = ({ value, onChange, options, testid }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full h-9 border rounded-md px-2 text-sm" data-testid={testid}>
    {Object.entries(options).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
  </select>
);

function NewInvoiceDialog({ open, onClose, onIssued }) {
  const [reservations, setReservations] = useState([]);
  const [f, setF] = useState(null);
  useEffect(() => {
    if (!open) return;
    api.get("/reservations").then(({ data }) => setReservations(data.filter((r) => r.status !== "CANCELLED"))).catch(() => {});
    setF({ reservation_id: "", customer: { ...emptyCustomer }, lines: [], payment_method: "CASH", payment_status: "PAID", notes: "" });
  }, [open]);
  if (!f) return null;
  const pickRes = async (id) => {
    const r = reservations.find((x) => x.id === id);
    if (!r) return setF({ ...f, reservation_id: "" });
    const company = await loadConfig("company");
    const vat = Number(company.rooms_vat_rate ?? 9);
    setF({ ...f, reservation_id: id, customer: { ...f.customer, name: `${r.first_name} ${r.last_name}`, email: r.email || "" },
      lines: [{ description: `Нощувка – стая ${r.room_number} ${r.room_name} (${r.arrival} – ${r.departure})`, quantity: r.nights, unit: "нощ.", unit_price: r.price_per_night, vat_rate: vat }],
      payment_status: r.paid >= r.total_price ? "PAID" : r.paid > 0 ? "PARTIAL" : "UNPAID", payment_method: r.payment_method || "CASH" });
  };
  const setLine = (i, patch) => setF({ ...f, lines: f.lines.map((l, j) => (j === i ? { ...l, ...patch } : l)) });
  const issue = async () => {
    if (!window.confirm("Издадената фактура не може да бъде променяна. Издаване?")) return;
    try {
      const { data } = await api.post("/invoices", { ...f, reservation_id: f.reservation_id || null,
        lines: f.lines.map((l) => ({ ...l, quantity: Number(l.quantity), unit_price: Number(l.unit_price), vat_rate: Number(l.vat_rate) })) });
      toast.success(`Фактура № ${data.number_str} е издадена`);
      onIssued(data);
    } catch (e) { toast.error(apiError(e)); }
  };
  const total = f.lines.reduce((s, l) => s + Number(l.quantity) * Number(l.unit_price), 0);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="new-invoice-dialog">
        <DialogHeader><DialogTitle>Нова фактура</DialogTitle></DialogHeader>
        <div><Label className="text-xs">Резервация (по избор)</Label>
          <select value={f.reservation_id} onChange={(e) => pickRes(e.target.value)} className="w-full h-9 border rounded-md px-2 text-sm" data-testid="invoice-reservation">
            <option value="">— без резервация —</option>
            {reservations.map((r) => <option key={r.id} value={r.id}>{r.first_name} {r.last_name} · стая {r.room_number} · {r.arrival} → {r.departure}</option>)}
          </select>
        </div>
        <div className="grid sm:grid-cols-2 gap-2">
          {[["name", "Име на клиента *"], ["company", "Фирма"], ["eik", "ЕИК"], ["vat_number", "ДДС №"], ["address", "Адрес"], ["mol", "МОЛ"]].map(([k, l]) => (
            <div key={k}><Label className="text-xs">{l}</Label><Input value={f.customer[k]} onChange={(e) => setF({ ...f, customer: { ...f.customer, [k]: e.target.value } })} data-testid={`invoice-customer-${k}`} /></div>
          ))}
        </div>
        <div className="space-y-2">
          <Label className="text-xs">Редове (цените са с включено ДДС)</Label>
          {f.lines.map((l, i) => (
            <div key={i} className="grid grid-cols-[1fr_70px_70px_90px_70px_auto] gap-1 items-center">
              <Input value={l.description} onChange={(e) => setLine(i, { description: e.target.value })} placeholder="Описание" data-testid={`invoice-line-desc-${i}`} />
              <Input type="number" value={l.quantity} onChange={(e) => setLine(i, { quantity: e.target.value })} title="Количество" data-testid={`invoice-line-qty-${i}`} />
              <Input value={l.unit} onChange={(e) => setLine(i, { unit: e.target.value })} title="Мярка" />
              <Input type="number" step="0.01" value={l.unit_price} onChange={(e) => setLine(i, { unit_price: e.target.value })} title="Ед. цена" data-testid={`invoice-line-price-${i}`} />
              <Input type="number" value={l.vat_rate} onChange={(e) => setLine(i, { vat_rate: e.target.value })} title="ДДС %" data-testid={`invoice-line-vat-${i}`} />
              <Button variant="ghost" size="icon" onClick={() => setF({ ...f, lines: f.lines.filter((_, j) => j !== i) })}><Trash2 className="w-4 h-4" /></Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => setF({ ...f, lines: [...f.lines, { description: "", quantity: 1, unit: "бр.", unit_price: 0, vat_rate: 20 }] })} className="gap-1" data-testid="invoice-add-line"><Plus className="w-4 h-4" /> Ред</Button>
        </div>
        <div className="grid sm:grid-cols-2 gap-2">
          <div><Label className="text-xs">Начин на плащане</Label><Sel value={f.payment_method} onChange={(v) => setF({ ...f, payment_method: v })} options={PAYMENT_METHODS} testid="invoice-payment-method" /></div>
          <div><Label className="text-xs">Статус на плащане</Label><Sel value={f.payment_status} onChange={(v) => setF({ ...f, payment_status: v })} options={PAYMENT_STATUSES} testid="invoice-payment-status" /></div>
        </div>
        <div><Label className="text-xs">Бележки</Label><Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
        <DialogFooter className="items-center gap-3">
          <span className="font-bold text-forest-deep" data-testid="invoice-new-total">Общо: {money(total)}</span>
          <Button onClick={issue} disabled={!f.customer.name || !f.lines.length} className="bg-forest text-white" data-testid="invoice-issue">Издай фактура</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InvoiceView({ inv, onClose, onCancelled, isOwner }) {
  const [saved, setSaved] = useState(INVOICE_DEFAULTS);
  useEffect(() => { loadConfig("print_invoice", INVOICE_DEFAULTS).then(setSaved).catch(() => {}); }, []);
  if (!inv) return null;
  const tpl = invoiceTpl(inv, saved);
  const cancel = async () => {
    const reason = window.prompt("Причина за анулиране");
    if (!reason) return;
    try { const { data } = await api.post(`/invoices/${inv.id}/cancel`, { reason }); onCancelled(data); toast.success("Фактурата е анулирана"); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open={!!inv} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[900px] max-h-[92vh] overflow-y-auto" data-testid="invoice-view">
        <DialogHeader><DialogTitle>Фактура № {inv.number_str}</DialogTitle></DialogHeader>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => printInvoice(tpl, inv)} className="gap-1 bg-forest text-white" data-testid="invoice-print"><Printer className="w-4 h-4" /> Печат A4</Button>
          <Button variant="outline" onClick={() => downloadInvoicePdf(tpl, inv).catch((e) => toast.error(e.message))} className="gap-1" data-testid="invoice-pdf"><FileDown className="w-4 h-4" /> PDF</Button>
          {isOwner && inv.status === "ISSUED" && <Button variant="outline" onClick={cancel} className="gap-1 text-destructive" data-testid="invoice-cancel"><Ban className="w-4 h-4" /> Анулирай</Button>}
        </div>
        <div className="bg-neutral-200 rounded-xl p-3 overflow-auto"><div className="origin-top-left scale-[0.8] w-fit shadow-lg"><InvoiceDoc tpl={tpl} inv={inv} /></div></div>
      </DialogContent>
    </Dialog>
  );
}

export default function Invoices() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [view, setView] = useState(null);
  const load = () => api.get("/invoices").then(({ data }) => setList(data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);
  return (
    <div className="p-4 sm:p-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h1 className="font-display text-4xl text-forest-deep">Фактури</h1><div className="gold-rule w-24 my-3" /></div>
        <Button onClick={() => setNewOpen(true)} className="bg-forest text-white gap-1" data-testid="new-invoice-btn"><Plus className="w-4 h-4" /> Нова фактура</Button>
      </div>
      <div className="bg-white rounded-2xl border border-gold/10 shadow-soft divide-y divide-gold/10 mt-4" data-testid="invoice-list">
        {list.map((i) => (
          <button key={i.id} onClick={() => setView(i)} className="w-full flex flex-wrap items-center gap-3 p-3 text-left hover:bg-cream-muted" data-testid={`invoice-row-${i.number}`}>
            <span className="font-mono font-semibold text-forest-deep">{i.number_str}</span>
            <span className="text-sm text-muted-foreground">{i.issue_date}</span>
            <span className="flex-1 min-w-[8rem]">{i.customer.company || i.customer.name}</span>
            {i.status === "CANCELLED" && <span className="text-xs bg-rose-100 text-rose-700 px-2 py-0.5 rounded">Анулирана</span>}
            <span className="font-bold">{money(i.totals.total)}</span>
          </button>
        ))}
        {list.length === 0 && <p className="p-6 text-muted-foreground text-sm">Все още няма издадени фактури.</p>}
      </div>
      <NewInvoiceDialog open={newOpen} onClose={() => setNewOpen(false)} onIssued={(inv) => { setNewOpen(false); load(); setView(inv); }} />
      <InvoiceView inv={view} onClose={() => setView(null)} isOwner={user.role === "OWNER"} onCancelled={(inv) => { setView(inv); load(); }} />
    </div>
  );
}
